package com.arena_web.core.viewmodel

import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.FollowRepository
import com.arena_web.domain.repositories.UserRepository
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class UserProfileViewModel(
    private val userId: String,
    private val userRepository: UserRepository,
    private val teamRepository: com.arena_web.domain.repositories.TeamRepository? = null,
    private val followRepository: FollowRepository? = null,
    private val eventRepository: com.arena_web.domain.repositories.EventRepository? = null,
    private val cardRepository: com.arena_web.domain.repositories.CardRepository? = null,
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ProfileUiState())
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadProfile()
        loadFollowRelation()
        loadFollowCounts()
    }

    fun loadProfile() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null, statsCharts = emptyMap()) }

            val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
            val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase()
            val hasSport = !sportId.isNullOrBlank()

            coroutineScope {
                val jobs = mutableListOf<kotlinx.coroutines.Deferred<*>>()

                jobs += async {
                    userRepository.getUser(userId)
                        .onSuccess { u -> _uiState.update { it.copy(user = u) } }
                        .onFailure { e -> _uiState.update { it.copy(error = e.message) } }
                }

                if (hasSport) {
                    jobs += async {
                        userRepository.getSportProfile(userId, mapOf("sportId" to sportId!!))
                            .onSuccess { profile -> _uiState.update { it.copy(sportProfile = profile, aggregateRating = null) } }
                            .onFailure { e -> logger.debug("Sport profile not available: ${e.message}") }
                    }
                    if (sportName != "other") {
                        jobs += async { loadStatsChart(_uiState.value.goalsChartPeriod) }
                        val assistsPeriod = _uiState.value.assistsChartPeriod
                        if (assistsPeriod != _uiState.value.goalsChartPeriod) {
                            jobs += async { loadStatsChart(assistsPeriod) }
                        }
                    }
                } else {
                    // "All" — fetch the cross-sport mean overall rating
                    // for this user. Sport-specific blocks (radar, stats,
                    // position) stay hidden in the UI.
                    jobs += async {
                        userRepository.getSportProfileAggregate(userId)
                            .onSuccess { agg -> _uiState.update { it.copy(sportProfile = null, aggregateRating = agg) } }
                            .onFailure { e -> logger.debug("Aggregate rating not available: ${e.message}") }
                    }
                }

                jobs += async {
                    userRepository.getUserFavoriteMedia(userId)
                        .onSuccess { media -> _uiState.update { it.copy(favoriteMedia = media) } }
                        .onFailure { e -> logger.debug("Failed to load favorite media: ${e.message}") }
                }

                jobs += async {
                    userRepository.getUserAwards(userId)
                        .onSuccess { awards -> _uiState.update { it.copy(awards = awards) } }
                        .onFailure { e -> logger.debug("Failed to load awards: ${e.message}") }
                }

                // Public profile-card gallery — feeds the mobile card carousel
                // (and its pagination dots). Read-only: no reorder/delete here.
                cardRepository?.let { repo ->
                    jobs += async {
                        repo.getUserProfileCards(userId)
                            .onSuccess { cards -> _uiState.update { it.copy(profileCards = cards) } }
                            .onFailure { e -> logger.debug("Failed to load profile cards: ${e.message}") }
                    }
                }

                // Recent events run on their own launch (see ProfileViewModel
                // for context) — the section shows an inline loader instead
                // of blocking the public profile's top-level isLoading.
                fetchMatchHistoryPage(page = 0)

                jobs.awaitAll()
            }

            _uiState.update { it.copy(isLoading = false) }
        }
    }

    /** Refetch sport-scoped data (sport profile + stat charts) when the
     *  viewer switches sport in the header. Public profile mirrors the
     *  own-profile flow — other state stays put. */
    fun reloadSportData() {
        viewModelScope.launch {
            _uiState.update { it.copy(statsCharts = emptyMap()) }

            val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
            val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase()
            if (sportId.isNullOrBlank()) {
                // "All" — clear per-sport profile, pull cross-sport mean.
                _uiState.update { it.copy(sportProfile = null) }
                userRepository.getSportProfileAggregate(userId)
                    .onSuccess { agg -> _uiState.update { it.copy(aggregateRating = agg) } }
                    .onFailure { e -> logger.debug("Aggregate rating not available: ${e.message}") }
                return@launch
            }

            coroutineScope {
                val jobs = mutableListOf<kotlinx.coroutines.Deferred<*>>()
                jobs += async {
                    userRepository.getSportProfile(userId, mapOf("sportId" to sportId))
                        .onSuccess { profile -> _uiState.update { it.copy(sportProfile = profile, aggregateRating = null) } }
                        .onFailure { e -> logger.debug("Sport profile not available: ${e.message}") }
                }
                if (sportName != "other") {
                    jobs += async { loadStatsChart(_uiState.value.goalsChartPeriod) }
                    val assistsPeriod = _uiState.value.assistsChartPeriod
                    if (assistsPeriod != _uiState.value.goalsChartPeriod) {
                        jobs += async { loadStatsChart(assistsPeriod) }
                    }
                }
                jobs.awaitAll()
            }
        }
    }

    /** Page navigation for the public-profile "Recent events" section. */
    fun loadMatchHistoryPage(page: Int) {
        if (page < 0) return
        fetchMatchHistoryPage(page)
    }

    private fun fetchMatchHistoryPage(page: Int) {
        val repo = eventRepository ?: return
        _uiState.update { it.copy(matchHistoryPage = page, matchHistoryLoading = true) }
        viewModelScope.launch {
            repo.getEvents(matchHistoryParams(page = page))
                .onSuccess { result ->
                    _uiState.update {
                        it.copy(
                            matchHistory = result.events,
                            matchHistoryPage = result.page,
                            matchHistoryTotal = result.total,
                            matchHistoryLoading = false,
                        )
                    }
                }
                .onFailure { e ->
                    logger.debug("Failed to load recent events page $page: ${e.message}")
                    _uiState.update { it.copy(matchHistoryLoading = false) }
                }
        }
    }

    private fun matchHistoryParams(page: Int): Map<String, String> =
        mapOf(
            "participantId" to userId,
            "status" to "COMPLETED",
            "page" to page.toString(),
            "size" to MATCH_HISTORY_PAGE_SIZE.toString(),
        )

    fun changeGoalsChartPeriod(period: String) {
        _uiState.update { it.copy(goalsChartPeriod = period) }
        viewModelScope.launch { loadStatsChart(period) }
    }

    fun changeAssistsChartPeriod(period: String) {
        _uiState.update { it.copy(assistsChartPeriod = period) }
        viewModelScope.launch { loadStatsChart(period) }
    }

    fun changeTeamChartPeriod(period: String) {
        _uiState.update { it.copy(teamChartPeriod = period, teamChart = null) }
        viewModelScope.launch {
            loadTeamChart(period)
        }
    }

    private suspend fun loadTeamData() {
        if (teamRepository == null) return

        userRepository.getUserTeams(userId).onSuccess { teams ->
            val firstTeam = teams.firstOrNull() ?: return@onSuccess

            teamRepository.getTeam(firstTeam.id).onSuccess { teamWithMembers ->
                _uiState.update { it.copy(team = teamWithMembers) }
            }.onFailure { e ->
                logger.debug("Failed to load team: ${e.message}")
            }

            teamRepository.getMatches(firstTeam.id).onSuccess { matches ->
                _uiState.update { it.copy(teamMatches = matches) }
            }.onFailure { e ->
                logger.debug("Failed to load team matches: ${e.message}")
            }

            loadTeamChart(_uiState.value.teamChartPeriod, firstTeam.id)
        }.onFailure { e ->
            logger.debug("Failed to load user teams: ${e.message}")
        }
    }

    private suspend fun loadTeamChart(period: String, teamId: String? = null) {
        val id = teamId ?: _uiState.value.team?.id
        if (id == null || teamRepository == null) return

        teamRepository.getChart(id, mapOf("period" to period)).onSuccess { chart ->
            _uiState.update { it.copy(teamChart = chart) }
        }.onFailure { e ->
            logger.debug("Failed to load team chart: ${e.message}")
        }
    }

    private suspend fun loadStatsChart(period: String) {
        if (_uiState.value.statsCharts.containsKey(period)) return
        val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID)
        if (sportId.isNullOrBlank()) return

        userRepository.getUserStatsChart(userId, mapOf("period" to period, "sportId" to sportId)).onSuccess { chart ->
            _uiState.update { it.copy(statsCharts = it.statsCharts + (period to chart)) }
        }.onFailure { e ->
            logger.debug("Stats chart not available: ${e.message}")
        }
    }

    // ── Follow ──

    private fun loadFollowRelation() {
        viewModelScope.launch {
            followRepository?.getRelation(userId)
                ?.onSuccess { rel ->
                    _uiState.update { it.copy(isFollowing = rel.isFollowing, isFollowedBy = rel.isFollowedBy) }
                }
                ?.onFailure {
                    _uiState.update { it.copy(isFollowing = false, isFollowedBy = false) }
                }
        }
    }

    private fun loadFollowCounts() {
        viewModelScope.launch {
            followRepository?.getCounts(userId)
                ?.onSuccess { counts ->
                    _uiState.update {
                        it.copy(followersCount = counts.followersCount, followingCount = counts.followingCount)
                    }
                }
        }
    }

    fun follow() {
        if (_uiState.value.isFollowing || _uiState.value.followActionLoading) return
        AnalyticsTracker.trackClick("follow_user", screen = "user_profile", params = mapOf("user_id" to userId))
        _uiState.update { it.copy(followActionLoading = true) }
        viewModelScope.launch {
            followRepository?.followUser(userId)
                ?.onSuccess {
                    _uiState.update {
                        it.copy(
                            isFollowing = true,
                            followersCount = (it.followersCount ?: 0L) + 1,
                            followActionLoading = false
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(error = e.message, followActionLoading = false) }
                }
        }
    }

    // ── Follow popups (read-only view of other user's followers/following) ──

    fun openFollowersDialog() {
        _uiState.update { it.copy(showFollowersDialog = true) }
        if (_uiState.value.followers.isEmpty()) reloadFollowers()
    }

    fun openFollowingDialog() {
        _uiState.update { it.copy(showFollowingDialog = true) }
        if (_uiState.value.following.isEmpty()) reloadFollowing()
    }

    fun closeFollowersDialog() {
        _uiState.update { it.copy(showFollowersDialog = false) }
    }

    fun closeFollowingDialog() {
        _uiState.update { it.copy(showFollowingDialog = false) }
    }

    private fun reloadFollowers() {
        viewModelScope.launch {
            _uiState.update { it.copy(followersListLoading = true) }
            followRepository?.getFollowers(userId, mapOf("page" to "1", "size" to "100"))
                ?.onSuccess { page ->
                    _uiState.update { it.copy(followers = page.items, followersListLoading = false) }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(followersListLoading = false, error = e.message) }
                }
                ?: _uiState.update { it.copy(followersListLoading = false) }
        }
    }

    private fun reloadFollowing() {
        viewModelScope.launch {
            _uiState.update { it.copy(followingListLoading = true) }
            followRepository?.getFollowing(userId, mapOf("page" to "1", "size" to "100"))
                ?.onSuccess { page ->
                    _uiState.update { it.copy(following = page.items, followingListLoading = false) }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(followingListLoading = false, error = e.message) }
                }
                ?: _uiState.update { it.copy(followingListLoading = false) }
        }
    }

    fun unfollow() {
        if (!_uiState.value.isFollowing || _uiState.value.followActionLoading) return
        _uiState.update { it.copy(followActionLoading = true) }
        viewModelScope.launch {
            followRepository?.unfollowUser(userId)
                ?.onSuccess {
                    _uiState.update {
                        it.copy(
                            isFollowing = false,
                            followersCount = ((it.followersCount ?: 0L) - 1).coerceAtLeast(0L),
                            followActionLoading = false
                        )
                    }
                }
                ?.onFailure { e ->
                    _uiState.update { it.copy(error = e.message, followActionLoading = false) }
                }
        }
    }
}
